import { motion } from 'motion/react';

interface SkeletonBlockProps {
  className?: string
  width?: string
  height?: string
}

interface ClassNameProps {
  className?: string
}

/**
 * Shimmer animation for loading states
 */
const shimmerClass = 'animate-shimmer bg-gradient-to-r from-charcoal/50 via-slate/50 to-charcoal/50 bg-[length:200%_100%]';

/**
 * Single skeleton line/block
 */
function SkeletonBlock({ className = '', width = 'w-full', height = 'h-4' }: SkeletonBlockProps) {
  return (
    <div className={`${shimmerClass} rounded ${width} ${height} ${className}`} />
  );
}

/**
 * Card skeleton for glass-card loading state
 */
export function SkeletonCard({ className = '' }: ClassNameProps) {
  return (
    <div className={`glass-card rounded-xl p-4 ${className}`}>
      <div className="space-y-3">
        <SkeletonBlock width="w-1/3" height="h-3" />
        <SkeletonBlock width="w-full" height="h-5" />
        <SkeletonBlock width="w-2/3" height="h-3" />
      </div>
    </div>
  );
}

/**
 * List item skeleton
 */
export function SkeletonListItem({ className = '' }: ClassNameProps) {
  return (
    <div className={`flex items-center gap-3 p-3 ${className}`}>
      <SkeletonBlock width="w-10" height="h-10" className="rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <SkeletonBlock width="w-1/2" height="h-4" />
        <SkeletonBlock width="w-3/4" height="h-3" />
      </div>
    </div>
  );
}

/**
 * Typing indicator (three bouncing dots)
 */
export function TypingIndicator({ className = '' }: ClassNameProps) {
  return (
    <div className={`flex items-center gap-1 p-3 ${className}`}>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="w-2 h-2 rounded-full bg-gold/60"
          animate={{
            y: [0, -6, 0],
          }}
          transition={{
            duration: 0.6,
            repeat: Infinity,
            delay: i * 0.15,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}
